// background.default (not .paper) throughout this file — was pure white,
// switched to match the Inbox list panel's own background and LeftNav's
// (both background.default) instead of standing out as a bright white bar
// (Rahim, 2026-08-20).
import ChatNumberBadge from "./ChatNumberBadge";
import { findInboxCard } from "../../../utils/findInboxCard";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import { checkBeforeOutbound, isSubscriptionBlockedError } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import { CHATBOX_NAVBAR_HEADER_TOTAL_PX, INBOX_HEADER_BORDER_PX } from "../../../utils/inboxLayoutConstants";
import {
  Avatar,
  Button,
  Typography,
  Box,
  Popover,
  Skeleton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import IconButtonWithTooltip from "../Shared/IconButtonWithTooltip";
import InfoIcon from "@mui/icons-material/Info";
import { ArrowDown2, Profile2User, TickCircle as TickCircleIx, UserAdd, UserRemove, CloseCircle } from "iconsax-react";
import { CircularProgress, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setChatInfo } from "../../../redux/Slice/Inbox/inboxSlice";
import AssignBox from "./AssignBox";
import AiAssignBox from "./AiAssignBox";
import { fetchAiAgents } from "../../../redux/Slice/AiAgents/aiAgentsSlice";
import { updateAssignmentInInbox } from "../../../redux/Slice/Inbox/inboxRecentSlice";
import { nameToContactAvatarImage, stringAvatar, AVATAR_SIZES, stringToColor, NON_DRAGGABLE_AVATAR_IMG_PROPS } from "../../../config/AvatarColor";
import { clearAssignState } from "../../../redux/Slice/Inbox/inboxSettingsSlice";
import { clearComposerActionRequest } from "../../../redux/Slice/Inbox/composerActionsSlice";
import { closeContact } from "../../../redux/Slice/Contacts/contactsSlice";
import { setContactInfo, invalidateCachedContact, addGroupParticipant, removeGroupParticipant } from "../../../redux/Slice/Inbox/inboxInfoSlice";
import { setAssignToAiLoading } from "../../../redux/Slice/Inbox/aiAssistStatusSlice";
import PlatformIcon from "./PlatformIcon";
import LifecycleStageChip from "../../Lifecycle/LifecycleStageChip";
import CallButton from "../../../features/calling/components/CallButton";
// "Assign to AI" icon — not in our iconsax-react package (that's the free-
// tier 993-icon set; this "ai-users" glyph is Iconsax's premium/paid one,
// which Rahim confirmed is licensed for use here, 2026-08-29). A real .svg
// asset + CRA's SVGR ReactComponent import, matching this codebase's actual
// convention for one-off custom icons (see ChatCard.jsx's Sent/Read/Error
// .svg imports) — not hardcoded inline JSX. stroke="currentColor" is baked
// into the source file so it still follows the button's text color like
// every other icon here.
import { ReactComponent as AiUsersIcon } from "../../../Images/Inbox/AiUsers.svg";

const ChatboxNavbar = ({ openDialog, setOpenDialog, isTeamMemberChat = false, otherMember = null }) => {
  // For team member chats, don't subscribe to user chat Redux state to prevent unnecessary re-renders
  const { messages, error, loading: messagesLoading } = useSelector((state) => isTeamMemberChat ? { messages: null, error: null, loading: false } : state.inboxMessage);
  const selectedContactID = useSelector((state) =>
    isTeamMemberChat ? null : state.inboxAll?.selectedContactID
  );
  const expectedContactId = useSelector((state) =>
    isTeamMemberChat ? null : state.inboxMessage?.expectedContactId
  );
  const dispatch = useDispatch();
  const { showChatInfo, screenSize } = useSelector((state) => state.inbox);
  const { contactInfo, groupInfo } = useSelector((state) => isTeamMemberChat ? { contactInfo: null, groupInfo: null } : state.inboxInfo);
  // WhatsApp Groups plan, Phase G7 follow-up: a group thread never has assignment/AI-takeover/
  // 24h-window/close-chat concepts (decision #2), so every one of those existing header pieces
  // below gets an additional !isGroup guard rather than being forked into two components.
  const isGroup = !isTeamMemberChat && !!messages?.isGroup;
  // 1:1: the inbox card already carries the customer's last number (see inboxController's lastConnectionId).
  const headerCardConnectionId = useSelector((state) => {
    if (isTeamMemberChat || isGroup || !selectedContactID) return null;
    // Any inbox list that holds the chat (a closed chat is not in `All`). A QR chat whose contact has no number of its own yet shows the
    // number of its latest message, as its card in the list does.
    const card = findInboxCard(state.inboxRecent?.data, selectedContactID);
    return card?.connectionId || (messages?.platform === "qr" ? card?.lastConnectionId : null) || null;
  });
  const [groupMembersAnchorEl, setGroupMembersAnchorEl] = useState(null);
  // Add/remove group member (WhatsApp Groups plan follow-up, 2026-09-26) -- all local to this
  // popover, reset whenever it closes (see the Popover's onClose below) so a stale search/error
  // never lingers into the next time it's opened.
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberQuery, setAddMemberQuery] = useState("");
  const [addMemberResults, setAddMemberResults] = useState([]);
  const [addMemberSearching, setAddMemberSearching] = useState(false);
  const [addingContactId, setAddingContactId] = useState(null);
  const [removingJid, setRemovingJid] = useState(null);
  const addMemberSearchDebounceRef = useRef(null);
  // Filter the already-loaded member list (client-side, no new request -- unlike addMemberQuery
  // above, which searches ClusterDen's own Contacts to find someone to add). Reset whenever the
  // popover closes, same as the add-member state.
  const [memberFilterQuery, setMemberFilterQuery] = useState("");

  const resetAddMemberState = () => {
    setAddMemberOpen(false);
    setAddMemberQuery("");
    setAddMemberResults([]);
    setAddMemberSearching(false);
    setAddingContactId(null);
  };

  // Debounced live search against the existing GET /contacts/get?search= endpoint (the same
  // one the main Contacts page already uses) -- deliberately no new backend search endpoint,
  // just a new consumer of one that already exists.
  useEffect(() => {
    if (!addMemberOpen) return undefined;
    if (addMemberSearchDebounceRef.current) clearTimeout(addMemberSearchDebounceRef.current);
    const term = addMemberQuery.trim();
    if (!term) {
      setAddMemberResults([]);
      setAddMemberSearching(false);
      return undefined;
    }
    setAddMemberSearching(true);
    addMemberSearchDebounceRef.current = setTimeout(async () => {
      try {
        const token = await getAuthToken();
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000"}/api/contacts/get`,
          { headers: { Authorization: `Bearer ${token}` }, params: { search: term, limit: 8 } }
        );
        setAddMemberResults(response.data?.contacts || []);
      } catch (_err) {
        setAddMemberResults([]);
      } finally {
        setAddMemberSearching(false);
      }
    }, 350);
    return () => {
      if (addMemberSearchDebounceRef.current) clearTimeout(addMemberSearchDebounceRef.current);
    };
  }, [addMemberQuery, addMemberOpen]);

  const handleAddParticipant = async (contact) => {
    if (!groupInfo?.groupID) return;
    setAddingContactId(contact._id);
    try {
      await dispatch(addGroupParticipant({ groupId: groupInfo.groupID, contactId: contact._id })).unwrap();
      toast.success(`${contact.name || "Contact"} added to the group`);
      resetAddMemberState();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to add member");
    } finally {
      setAddingContactId(null);
    }
  };

  const handleRemoveParticipant = async (participant) => {
    if (!groupInfo?.groupID) return;
    setRemovingJid(participant.jid);
    try {
      await dispatch(removeGroupParticipant({ groupId: groupInfo.groupID, participantJid: participant.jid })).unwrap();
      toast.success(`${participant.name || participant.phoneNumberID || "Member"} removed from the group`);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to remove member");
    } finally {
      setRemovingJid(null);
    }
  };
  const { assignStatus } = useSelector((state) => isTeamMemberChat ? { assignStatus: null } : state.inboxSettings);
  const currentTeamMemberChat = useSelector((state) => state.teamMemberChat?.currentChat);
  const workspaceId = useSelector((state) => state.workspace?.currentWorkspace?._id);

  // Client-side filter over the already-loaded member list -- groups top out well under a
  // scale where server-side search would matter, so no new endpoint for this.
  const filteredGroupMembers = useMemo(() => {
    const all = groupInfo?.participants || [];
    const term = memberFilterQuery.trim().toLowerCase();
    if (!term) return all;
    return all.filter(
      (p) => (p.name || "").toLowerCase().includes(term) || (p.phoneNumberID || "").includes(term)
    );
  }, [groupInfo?.participants, memberFilterQuery]);

  const theme = useTheme();
  const [closingConversation, setClosingConversation] = useState(false);
  // "Assign to AI" button — settings check + loading state (from stagingmerge's
  // AI Inbox Assist). Moved here from TextBox.jsx's composer dropdown (Rahim,
  // 2026-08-29: "take it to the top nav and use it as a button"). Take Back
  // Conversation stays composer-only (TextBox.jsx's AI-active banner) — this
  // button only ever enables, never disables, so no overlap with that control.
  const [aiTakeoverEnabled, setAiTakeoverEnabled] = useState(false);
  // Sourced from Redux (not local state) — same flag TextBox.jsx's composer
  // loader reads, single source of truth, avoids a name collision with the
  // imported setAssignToAiLoading action below.
  const assignToAiLoading = useSelector((state) => state.aiAssistStatus?.assignToAiLoading);
  const aiTakeoverSettingsFetchedForWorkspaceRef = useRef(null);

  useEffect(() => {
    if (!workspaceId) return;
    if (aiTakeoverSettingsFetchedForWorkspaceRef.current === workspaceId) return;
    aiTakeoverSettingsFetchedForWorkspaceRef.current = workspaceId;
    (async () => {
      try {
        const token = await getAuthToken();
        const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
        const res = await axios.get(
          `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAiTakeoverEnabled(!!res.data?.inboxAssist?.takeoverEnabled);
      } catch (err) {
        console.warn("Failed to load Inbox AI Assist settings:", err.message);
      }
    })();
  }, [workspaceId]);

  // Roster for the "Assign to AI" dropdown (Rahim, 2026-09-01: "a dropdown will open
  // of all active ai agents" instead of always using the workspace's single default
  // agent). Same aiAgentsSlice roster InboxAll.jsx/InboxSettings.jsx already fetch —
  // fetched here too (cached thunk, so this is a no-op if one of those already has)
  // since ChatboxNavbar can't assume either of them mounted first.
  const aiAgents = useSelector((state) => state.aiAgents?.agents || []);
  const aiAgentsLoading = useSelector((state) => state.aiAgents?.loading || false);
  const aiAgentsFetchedForWorkspaceRef = useRef(null);
  useEffect(() => {
    if (!workspaceId) return;
    if (aiAgentsFetchedForWorkspaceRef.current === workspaceId) return;
    aiAgentsFetchedForWorkspaceRef.current = workspaceId;
    dispatch(fetchAiAgents({ workspaceId }));
  }, [dispatch, workspaceId]);

  // Which agent row is currently mid-assignment — drives that row's own spinner in
  // AiAssignBox, separate from assignToAiLoading (Redux, drives TextBox.jsx's
  // composer-wide "Pondering..." loader regardless of which agent was picked).
  const [assigningAiAgentId, setAssigningAiAgentId] = useState(null);

  // Hands this one conversation to a specific AI agent (server:
  // eventHandlers/aiTakeoverEventHandler.js). Single request — the enable endpoint's
  // own response already carries the updated aiTakeover object, patched straight into
  // Redux below (see the comment inline) instead of a second fetchContactById round-trip.
  const handleAssignToAI = async (agent) => {
    const contactID = contactInfo?._id || selectedContactID;
    if (!contactID || !workspaceId || !agent?._id || assignToAiLoading) return;
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    setAiAssignAnchorEl(null);
    setAssigningAiAgentId(agent._id);
    dispatch(setAssignToAiLoading(true)); // drives TextBox.jsx's "Pondering..." composer loader too
    try {
      const token = await getAuthToken();
      const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      // The enable endpoint already returns the updated aiTakeover object
      // (server/controllers/inboxAssistController.js's own findOneAndUpdate
      // result) — patch that straight into Redux instead of firing a second
      // fetchContactById round-trip just to read back what we already have
      // (Rahim, 2026-08-29: "cant we make it only one call?"). Still
      // invalidate the cache (no network cost, just clears the stale entry)
      // so a LATER fetchContactById from elsewhere in the app doesn't serve
      // back the pre-takeover snapshot.
      const res = await axios.post(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/takeover/${contactID}/enable`,
        { agentId: agent._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Toast fires first (Rahim, 2026-08-29: "text box changes before the
      // notification pops up") — react-hot-toast's own pop-in animation needs
      // a head start, since the dispatch right after this triggers TextBox's
      // Fade-wrapped composer to start its own transition on the same tick.
      toast.success(`${agent.name || "AI"} is now handling this conversation`);
      invalidateCachedContact(contactID);
      dispatch(setContactInfo({ aiTakeover: res.data?.aiTakeover }));
      // Instant local feedback for the Inbox LIST card too — dispatch(setContactInfo)
      // above only ever patches this file's own contactInfo (inboxInfoSlice), not
      // inboxRecentSlice's copy MessageCard.jsx actually renders from, so without
      // this the card only updated once the live socket round-trip arrived (a real
      // gap: MessageCard.jsx's own AI-assign flow already does this same dispatch,
      // this one never did — Rahim, 2026-09-01: "the name in messageCard didn't
      // display at runtime").
      dispatch(updateAssignmentInInbox({
        contactId: contactID,
        assignedTo: agent._id,
        assignedToName: agent.name,
        assignedToId: agent._id,
        assignedToIsAi: true,
        status: "Assigned",
      }));
    } catch (err) {
      console.error("Failed to enable AI Takeover:", err);
      toast.error(err.response?.data?.message || "Failed to assign this conversation to AI");
    } finally {
      dispatch(setAssignToAiLoading(false));
      setAssigningAiAgentId(null);
    }
  };
  // Name for the "Assigned to AI" status display below — contactInfo.aiTakeover
  // only ever carries {enabled, agentId} (getContactById.service.js), so the
  // display name is resolved from the same aiAgents roster the picker itself uses.
  const currentAiAgentName = React.useMemo(() => {
    const agentId = contactInfo?.aiTakeover?.agentId;
    if (!agentId) return null;
    return aiAgents.find((agent) => String(agent._id) === String(agentId))?.name || null;
  }, [contactInfo?.aiTakeover?.agentId, aiAgents]);

  // Anchors the Assign dropdown to the name/chevron trigger instead of a
  // centered modal — same anchored-popup pattern as the Assign submenu in
  // MessageCard.jsx's "..." menu (Rahim, 2026-08-20).
  const [assignAnchorEl, setAssignAnchorEl] = useState(null);
  // Stable anchor node for the Assign Popover — see its ref={} usage below
  // for why this isn't just e.currentTarget (2026-08-21 glitch fix).
  const assignTriggerRef = useRef(null);
  // Same anchored-popup pattern as the human Assign dropdown above, for the
  // "Assign to AI" button's agent picker.
  const [aiAssignAnchorEl, setAiAssignAnchorEl] = useState(null);
  const aiAssignTriggerRef = useRef(null);

  // Note: The component will automatically re-render when contactInfo changes
  // because it's using useSelector which subscribes to Redux state changes
  // No need for explicit socket listeners here - Redux state updates will trigger re-render

  // Closes the conversation immediately on click — no confirmation popup.
  const handleCloseConversation = async () => {
    if (closingConversation) return;
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    const contactId = contactInfo?._id || selectedContactID;
    if (!contactId) {
      toast.error("No contact selected");
      return;
    }
    setClosingConversation(true);
    dispatch(closeContact({ id: contactId, closeData: { status: "Closed" } }))
      .unwrap()
      .then(() => {
        // Stay on this chat instead of navigating away/clearing it (Rahim,
        // 2026-09-08: "the chat should remain there, it's fine to show it")
        // -- the backend's close already emits a socket event (see
        // closedContact.service.js's socketHandler.receive_message/
        // newinboxmessage calls) that appends the "Chat Closed by ..."
        // system message and patches contactInfo.status live, same path
        // ChatInfo.jsx already listens for, so the panel reflects the new
        // Closed state on its own without force-clearing it here.
        toast.success("Conversation Closed");
      })
      .catch((error) => {
        console.error("Failed to Close contact:", error);
        const msg =
          typeof error === "string"
            ? error
            : error?.message || "Failed to close the conversation";
        if (!isSubscriptionBlockedError(msg)) {
          toast.error(msg);
        }
      })
      .finally(() => setClosingConversation(false));
  };

  // Bridges ChatActionBox.jsx's "Assign"/"Close conversation" menu items to
  // this component's own local Assign-dropdown/Close-conversation logic —
  // see composerActionsSlice.js for why this exists (those menu items were
  // calling setOpenDialog("assign")/("close"), which nothing ever consumed).
  const composerActionRequest = useSelector((state) => state.composerActions?.request);
  useEffect(() => {
    if (!composerActionRequest || isTeamMemberChat) return;
    if (composerActionRequest.type === "assign") {
      setAssignAnchorEl(assignTriggerRef.current);
    } else if (composerActionRequest.type === "close") {
      handleCloseConversation();
    }
    dispatch(clearComposerActionRequest());
    // handleCloseConversation is redefined every render (closes over
    // contactInfo etc.) — depending on the request's nonce alone, not the
    // function identity, is deliberate here (fire-once-per-signal effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerActionRequest, isTeamMemberChat, dispatch]);

  // Loading, error, or empty messages handling
  // if (loading) {
  //   return (
  //     <Box
  //       display="flex"
  //       alignItems="center"
  //       justifyContent="center"
  //       p={2}
  //       bgcolor="#F9F9F9"
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  // With no chat open, errors use ChatBox Snackbar only (avoids duplicate with a navbar strip).
  const showNavbarError =
    error &&
    (isTeamMemberChat || selectedContactID || messages?.contactID);

  if (showNavbarError) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={2}
        bgcolor={(theme) => theme.palette.background.default} // Theme-aware background
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Handle team member chat
  if (isTeamMemberChat) {
    const otherMemberName = otherMember?.name || currentTeamMemberChat?.otherMember?.name || 
                           (currentTeamMemberChat?.otherMemberId ? "Team Member" : null);
    
    if (otherMemberName) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // Keep xs left space consistent with customer chat navbar so back button doesn't overlap avatar
            padding: {
              xs: "9px 17.6px 0 40px",
              sm: "6px 20px",
              md: "6px 20px",
              lg: "6px 20px",
              xl: "6px 20px",
            },
            backgroundColor: (theme) => theme.palette.background.default,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            // lg/xl: shared target minus this row's own 6px+6px padding + 1px
            // border, matching InboxAll.jsx's own header row so the two panels'
            // top edges line up (Rahim, 2026-08-19). Derived from
            // inboxLayoutConstants.js, not hardcoded — see that file for why
            // this total differs from InboxAll.jsx's by 1px.
            minHeight: { xs: "38px", sm: "38px", md: "46px", lg: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 12 - INBOX_HEADER_BORDER_PX}px`, xl: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 12 - INBOX_HEADER_BORDER_PX}px` },
            height: { xs: "38px", sm: "38px", md: "46px", lg: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 12 - INBOX_HEADER_BORDER_PX}px`, xl: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 12 - INBOX_HEADER_BORDER_PX}px` },
          }}
        >
          <Box display="flex" alignItems="center" gap={1.3}>
            <Avatar 
              {...stringAvatar(otherMemberName, AVATAR_SIZES.MEDIUM)} 
              sx={{
                bgcolor: stringToColor(otherMemberName), // Use exact same color generation as user conversations
                color: "Black", // Ensure text color is black like user conversations
                width: { xs: 25, sm: 30, md: 30, lg: 33, xl: 35 },
                height: { xs: 25, sm: 30, md: 30, lg: 33, xl: 35 },
                fontSize: { xs: "1rem", sm: "0.9rem", md: "1rem", lg: "1.05rem", xl: "1.1rem" },
              }}
            />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: { xs: "1rem", sm: "1rem", md: "1rem", lg: "1rem", xl: "1rem" }, 
                  fontWeight: { xs: 600, sm: 600, md: 600, lg: 600, xl: 700 },
                  lineHeight: { xs: "0.7", sm: "1.2", md: "1.2", lg: "1.2", xl: "1.2" },
                  paddingBottom: 0,
                  marginBottom: 0,
                }}
              >
                {otherMemberName}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: { xs: "0.75rem", sm: "0.75rem", md: "0.75rem", lg: "0.75rem", xl: "0.75rem" },
                  lineHeight: { xs: "1", sm: "1", md: "1", lg: "1", xl: "1" },
                  paddingTop: 0,
                  marginTop: { xs: "-2px", sm: 0, md: 0, lg: 0, xl: 0 },
                }}
              >
                Team Member
              </Typography>
            </Box>
          </Box>
          {/* Info icon hidden for team member chats - ChatInfo is not available.
              A close/dismiss icon was tried here (2026-08-27) then reverted per
              Rahim's call ("no need to add this") — nothing renders on the right
              side of this header for team-member chats. */}
        </Box>
      );
    }
    return <Box></Box>;
  }

  // Show skeleton navbar while loading so the header area never flashes empty
  const isLoading = messagesLoading || (!messages?.contactName && (selectedContactID || expectedContactId));
  if (!messages?.contactName) {
    if (isLoading) {
      return (
        // Mirrors the real header below: avatar + name + lifecycle chip on
        // the left, Assign-to-AI/assignee-pill/call/close on the right —
        // not the old generic avatar+2-lines bar (Rahim, 2026-08-29: "the
        // top nav is still using previous layout skeleton use our new one").
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: { xs: "9px 17.6px 0 16px", sm: "9px 17.6px 0 17.6px", md: "9px 17.6px 0 17.6px", lg: "9px 17.6px 0 17.6px", xl: "9px 17.6px 0 17.6px" },
            backgroundColor: (theme) => theme.palette.background.default,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            minHeight: { xs: "40px", sm: "40px", md: "40px", lg: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 9 - INBOX_HEADER_BORDER_PX}px`, xl: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 9 - INBOX_HEADER_BORDER_PX}px` },
            paddingTop: { xs: "9px", sm: "9px", md: "9px", lg: "9px", xl: "9px" },
            paddingBottom: 0,
          }}
        >
          <Skeleton
            variant="circular"
            width={42}
            height={42}
            sx={{ mr: 0.75, flexShrink: 0, display: { xs: "none", sm: "block" } }}
          />
          <Skeleton
            variant="circular"
            width={36}
            height={36}
            sx={{ mr: 0.5, flexShrink: 0, display: { xs: "block", sm: "none" } }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Skeleton variant="text" width={120} height={22} />
            <Skeleton variant="rounded" width={54} height={20} sx={{ borderRadius: "20px" }} />
          </Box>
          <Skeleton variant="rounded" width={112} height={30} sx={{ borderRadius: "8px", ml: 1.25, mr: 1.25, flexShrink: 0, display: { xs: "none", sm: "block" } }} />
          <Skeleton variant="text" width={70} height={22} sx={{ mr: 0.75, flexShrink: 0 }} />
          <Skeleton variant="circular" width={22} height={22} sx={{ mx: 0.75, flexShrink: 0 }} />
          <Skeleton variant="rounded" width={72} height={30} sx={{ borderRadius: "8px", flexShrink: 0, display: { xs: "none", sm: "block" } }} />
        </Box>
      );
    }
    // lg/xl: shared target minus this row's own 1px border, matching
    // InboxAll.jsx's header exactly (live-measured 1px residual after the
    // first pass — Rahim, 2026-08-19). Derived from inboxLayoutConstants.js.
    return <Box sx={{ minHeight: { xs: "38px", sm: "38px", md: "46px", lg: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - INBOX_HEADER_BORDER_PX}px`, xl: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - INBOX_HEADER_BORDER_PX}px` }, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.default }} />;
  }

  // Brighter brand green on dark surfaces (primary.main is too low-contrast on paper)
  const assignAccentColor =
    theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.main;

  // Shared chevron — same iconsax ArrowDown2 used for every other dropdown
  // trigger in Inbox (MessageCard's "..." menu, InboxSettings' rail sections)
  // instead of the old plain "▼" text glyph (Rahim, 2026-08-20).
  const AssignChevron = ({ color }) => (
    <Box sx={{ width: 15, height: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color }}>
      {/* Thin outline chevron, not the solid-wedge Bold variant — matches
          InboxSettings.jsx's own ArrowDown2 usage (default/Linear variant)
          (Rahim, 2026-08-21: "change the drop down icon"). */}
      <ArrowDown2 size="100%" color="currentColor" />
    </Box>
  );

  // Determine the text and color for the assigned status
  const renderAssignedStatus = () => {
    // AI Takeover is now a real reassignment (see inboxAssistController.js's
    // enableTakeover): the contact's own status/assignedTo genuinely become
    // "Unassigned"/null while AI holds it, so without this early-out this whole
    // function would fall through to the generic status branch below, show
    // "Unassigned", and — worse — stay clickable into the team/team-member
    // AssignBox dropdown even though a human reassigning it makes no sense while
    // AI owns it. The "Assign to AI" button slot below is the single place that
    // state renders now (Rahim, 2026-09-01: "show in same button Assign to AI,
    // instead going to teamMember, team Dropdown").
    if (contactInfo?.aiTakeover?.enabled) return null;

    // Assigned to a specific team member — show their avatar + name + a
    // dropdown chevron, matching the agent-row convention used in the
    // Transfer menu (avatar, proper name font, not a small caption pill)
    // instead of a plain text line (Rahim, 2026-08-20).
    if (contactInfo?.status === "Assigned" && contactInfo?.assignedToName) {
      return (
        // Full name on hover — the name below is ellipsis-truncated at a
        // fixed maxWidth, so a long member/team name silently clips with no
        // way to read the rest (Rahim, 2026-08-29: "the name will be waay
        // too long so its better to add a tooltip for that as well").
        <Tooltip title={contactInfo.assignedToName} componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
        <Box
          onClick={() => setAssignAnchorEl(assignTriggerRef.current)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.625,
            cursor: "pointer",
            minWidth: 0,
            // Was cursor-only — nothing signaled "clickable" on hover besides
            // that (Rahim, 2026-08-21). Same rail-row hover formula used
            // everywhere else in Inbox (InboxSettings.jsx, AssignBox.jsx rows).
            borderRadius: "8px",
            px: 0.5,
            py: 0.625,
            mx: -0.5,
            my: -0.625,
            "&:hover": {
              backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
            },
          }}
        >
          <Avatar
            {...stringAvatar(contactInfo.assignedToName, 24)}
            sx={{
              bgcolor: stringToColor(contactInfo.assignedToName),
              color: "Black",
              width: 24,
              height: 24,
              fontSize: "0.75rem",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              // Neutral dark grey, regular weight — not the green assign-
              // accent (reserved for status text like Assigned/Closed) and
              // not bold/pitch-black either, a person's name here should
              // read as secondary info, not a heading (Rahim, 2026-08-20).
              color: (theme) => theme.palette.text.secondary,
              // Bumped — was reading too small/hard to see (Rahim, 2026-08-21).
              fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { xs: 90, sm: 130, md: 130, lg: 130, xl: 130 },
            }}
          >
            {contactInfo.assignedToName}
          </Typography>
          <AssignChevron color={theme.palette.text.secondary} />
        </Box>
        </Tooltip>
      );
    }

    let statusText = "Unassigned";
    let statusColor = theme.palette.text.secondary; // Default color

    // Handle the remaining status types
    if (contactInfo?.status === "Closed") {
      statusText = "Closed";
      statusColor =
        theme.palette.mode === "dark"
          ? theme.palette.success.light
          : theme.palette.success.main;
    } else if (contactInfo?.status === "Assigned") {
      if (contactInfo?.assignedToTeam) {
        // Assigned to a team
        statusText = `Assigned to Team ${contactInfo.assignedToTeam}`;
        statusColor = assignAccentColor;
      } else if (contactInfo?.assignedTo) {
        // Fallback to ID if no display name
        statusText = `Assigned to ${contactInfo.assignedTo}`;
        statusColor = assignAccentColor;
      } else {
        statusText = "Assigned";
        statusColor = assignAccentColor;
      }
    } else if (contactInfo?.status === "Unassigned") {
      // Neutral, not warning-orange — the icon badge already communicates
      // "unassigned" on its own, the text doesn't need to shout too
      // (Rahim, 2026-08-20: "dont colour the word unassigned keep it simple").
      statusText = "Unassigned";
      statusColor = theme.palette.text.secondary;
    } else {
      // Handle different assign status messages
      if (assignStatus === "success") {
        statusText = "Assigned successfully!";
        statusColor =
          theme.palette.mode === "dark"
            ? theme.palette.success.light
            : theme.palette.success.main;
      } else if (assignStatus === "failed") {
        statusText = "Assignment failed. Retry?";
        statusColor =
          theme.palette.mode === "dark"
            ? theme.palette.error.light
            : theme.palette.error.main;
      }
    }

    return (
      <Typography
        variant="body2"
        sx={{
          color: statusColor,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          // Same hover feedback as the assigned-agent branch above — was
          // cursor-only before (Rahim, 2026-08-21).
          borderRadius: "8px",
          px: 0.5,
          py: 0.625,
          mx: -0.5,
          my: -0.625,
          "&:hover": {
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
          },
          // Bumped — was reading too small/hard to see (Rahim, 2026-08-21).
          fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
          // Just a little bold — was default/normal weight (Rahim, 2026-08-21).
          fontWeight: 500,
          lineHeight: { xs: "normal", sm: "normal", md: "normal", lg: "normal", xl: "1" },
        }}
        onClick={() => setAssignAnchorEl(assignTriggerRef.current)}
      >
        {statusText === "Unassigned" && (
          // Exact same "Unassigned" badge as Calls rows (LiveCallCard.jsx):
          // grey circle, outline (not filled) person-group glyph
          // (Rahim, 2026-08-20: "border black and no fill colour").
          <Box sx={{ width: 19, height: 19, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, bgcolor: theme.palette.action.selected, color: theme.palette.text.secondary }}>
            <Profile2User size="12" variant="Linear" color="currentColor" />
          </Box>
        )}
        {statusText}
        <AssignChevron color={statusColor} />
      </Typography>
    );
  };

  return (
    <Box
      display="flex"
      alignItems="center"
        padding={{ xs: "9px 17.6px 0 40px", sm: "9px 17.6px 0 17.6px", md: "9px 17.6px 0 17.6px", lg: "9px 17.6px 0 17.6px", xl: "9px 17.6px 0 17.6px" }}
      bgcolor={(theme) => theme.palette.background.default} // Theme-aware background
      sx={{
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`, // Theme-aware border
          // lg/xl: shared target minus this row's own 9px top padding + 1px
          // border, matching InboxAll.jsx's own header row (Rahim, 2026-08-19:
          // the white-gap seam). Derived from inboxLayoutConstants.js.
          minHeight: { xs: "40px", sm: "40px", md: "40px", lg: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 9 - INBOX_HEADER_BORDER_PX}px`, xl: `${CHATBOX_NAVBAR_HEADER_TOTAL_PX - 9 - INBOX_HEADER_BORDER_PX}px` },
          overflow: { xs: "hidden", sm: "hidden", md: "hidden", lg: "hidden", xl: "hidden" },
          paddingTop: { xs: "9px", sm: "9px", md: "9px", lg: "9px", xl: "9px" },
          paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
      }}
    >
      {/* Mock contact avatar — matches the contact list card */}
        {/* mt counters the row's own asymmetric padding (paddingTop:9px,
            paddingBottom:0 — tied to the InboxAll divider-alignment math,
            not safe to change here) which otherwise sits alignItems:center
            content a few px below the row's true visual middle — more
            noticeable now the avatar is bigger (Rahim, 2026-08-20). */}
        <Box sx={{ mr: { xs: 0.5, sm: 0.75, md: 0.75, lg: 0.75, xl: 0.75 }, mt: "-7px", flexShrink: 0 }}>
          {/* position:relative wrapper so the platform badge (WhatsApp/etc)
              can pin to the avatar's own corner instead of sitting after the
              name — same convention as MessageCard.jsx's row avatar
              (Rahim, 2026-08-20: "place it in the bottom right of the pfp"). */}
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Avatar
              src={nameToContactAvatarImage(messages.contactName)}
              imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
              sx={{
                // Bumped up further — was still small next to the redesigned
                // Chats/Calls row avatars (44-50px) elsewhere in Inbox (Rahim,
                // 2026-08-20).
                width: { xs: 36, sm: 42, md: 42, lg: 42, xl: 42 },
                height: { xs: 36, sm: 42, md: 42, lg: 42, xl: 42 },
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.07)"
                    : "rgba(0,0,0,0.05)",
                border: (theme) => `1.5px solid ${theme.palette.divider}`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -2,
                right: -2,
                borderRadius: "50%",
                border: (theme) => `2px solid ${theme.palette.background.default}`,
                // display:flex — without it this ring's shrink-to-fit sizing
                // pulls in the surrounding font-size context via inline
                // baseline rules and can render as a slight oval instead of
                // a true circle (same fix as MessageCard.jsx/LiveCallCard.jsx).
                display: "flex",
              }}
            >
              <PlatformIcon platform={messages.platform || "whatsapp"} size={18} sx={{ ml: 0 }} />
            </Box>
          </Box>
      </Box>

      {/* Name and status text — mt corrects the same row-padding asymmetry
          (paddingTop:9px, paddingBottom:0) already corrected for the avatar
          wrapper; this sibling never got the same fix (Rahim, 2026-08-21,
          live-measured: "more space from the top"). */}
        <Box flexGrow={1} sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", mt: "-5.7px" }}>
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              color: (theme) => theme.palette.text.primary,
              // Bumped from 0.875rem/max — read small next to the bigger
              // avatar; letterSpacing tightened slightly for a crisper,
              // less "default h6" look (Rahim, 2026-08-20: "better font").
              fontSize: { xs: "0.9375rem", sm: "1.0625rem", md: "1.0625rem", lg: "1.0625rem", xl: "1.0625rem" },
              fontWeight: 600,
              letterSpacing: "-0.01em",
              lineHeight: { xs: "1.2", sm: "1.2", md: "1.2", lg: "1.2", xl: "1.2" },
              whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "nowrap" },
              overflow: { xs: "hidden", sm: "hidden", md: "hidden", lg: "hidden", xl: "hidden" },
              textOverflow: { xs: "ellipsis", sm: "ellipsis", md: "ellipsis", lg: "ellipsis", xl: "ellipsis" },
              marginBottom: { xs: "2px", sm: "2px", md: "2px", lg: "2px", xl: "2px" },
            }}
          >
            {messages.contactName}
          </Typography>
          {/* Same chip already used on Chats/Calls rows (MessageCard.jsx,
              LiveCallCard.jsx) — self-contained, renders nothing when no
              stage is set, updates via the same socket-driven contactInfo
              refresh this file already relies on (Rahim, 2026-08-21: "add
              the lifecycle icon... right beside the contact name"). */}
          <Box sx={{ ml: 1.5, flexShrink: 0, mt: "-2px" }}>
            <LifecycleStageChip
              contactId={contactInfo?._id || selectedContactID}
              lifecycleStageId={contactInfo?.lifecycleStageId}
              variant="solid"
              // The socket-driven refresh this file's own top comment relies on
              // only ever patches the Inbox LIST's copy of the contact
              // (inboxRecentSlice, via handleContactLifecycleUpdated in
              // socketHandler.js) — never this file's own contactInfo
              // (inboxInfoSlice), so without this the navbar's chip stayed on
              // the old stage after a change made from here (Rahim, 2026-08-21:
              // "it doesnt change from the top nav"). Patch it directly instead
              // of waiting on a refresh path that was never wired for this field.
              onChanged={(newStageId) => dispatch(setContactInfo({ lifecycleStageId: newStageId }))}
            />
          </Box>
        </Box>
      </Box>

      {/* "Assign to AI" — moved here from the composer's dropdown (Rahim,
          2026-08-29: "add it left of the assign dropdown buttoon"). Opens a
          dropdown of the workspace's active AI agents (Rahim, 2026-09-01) rather
          than always using the single default agent — see aiAssignAnchorEl/
          AiAssignBox below.
          Once AI actually owns the contact, this SAME slot switches to a plain
          "Assigned to AI" status display instead of hiding — the human assigned-
          status pill below (renderAssignedStatus) is suppressed for that case
          entirely, so this is the one place that state is shown, and it never
          opens the team/team-member AssignBox dropdown (Rahim, 2026-09-01: "show
          in same button Assign to AI, instead going to teamMember, team
          Dropdown"). Take Back stays a composer-only control (TextBox.jsx's
          AI-active banner) — no toggle-back action lives here either way. */}
      {!isTeamMemberChat && !isGroup && (aiTakeoverEnabled || contactInfo?.aiTakeover?.enabled) && (
        contactInfo?.aiTakeover?.enabled ? (
          <Tooltip title="AI is handling this conversation — use Take Back in the message box to reassign it" componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
            <Box sx={{ mt: "-7.7px", ml: { xs: 0.75, sm: 1.25, md: 1.25, lg: 1.25, xl: 1.25 } }}>
              <Button
                variant="outlined"
                size="small"
                disabled
                startIcon={
                  <Box sx={{ width: { xs: 16, sm: 18, md: 18, lg: 18, xl: 18 }, height: { xs: 16, sm: 18, md: 18, lg: 18, xl: 18 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AiUsersIcon width="100%" height="100%" />
                  </Box>
                }
                sx={{
                  mr: { xs: 0.75, sm: 1.25, md: 1.25, lg: 1.25, xl: 1.25 },
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
                  py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
                  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
                  // Purple AI accent (matches TextBox.jsx's "AI Smart Reply" badge)
                  // instead of MUI's default greyed-out disabled look — this is a
                  // status display, not a genuinely inert control.
                  "&.Mui-disabled": {
                    color: "#7C7BFF",
                    borderColor: "#7C7BFF",
                    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(124,123,255,0.12)" : "rgba(124,123,255,0.08)"),
                    "& .MuiButton-startIcon svg": { color: "#7C7BFF" },
                  },
                }}
              >
                {currentAiAgentName ? `Assigned to ${currentAiAgentName}` : "Assigned to AI"}
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <Box
            ref={aiAssignTriggerRef}
            onClick={() => setAiAssignAnchorEl(aiAssignTriggerRef.current)}
            sx={{ mt: "-7.7px", ml: { xs: 0.75, sm: 1.25, md: 1.25, lg: 1.25, xl: 1.25 } }}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={assignToAiLoading}
              startIcon={
                <Box sx={{ width: { xs: 16, sm: 18, md: 18, lg: 18, xl: 18 }, height: { xs: 16, sm: 18, md: 18, lg: 18, xl: 18 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AiUsersIcon width="100%" height="100%" />
                </Box>
              }
              endIcon={
                <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ArrowDown2 size="100%" color="currentColor" />
                </Box>
              }
              sx={{
                // Matches the ml on this button's own wrapper Box — even
                // breathing room on both sides instead of Close's tighter mr
                // (Rahim, 2026-08-29: "add right space of the assign to ai
                // button").
                mr: { xs: 0.75, sm: 1.25, md: 1.25, lg: 1.25, xl: 1.25 },
                borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.background.default,
                borderColor: (theme) => theme.palette.divider,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                  borderColor: (theme) => theme.palette.divider,
                },
                color: (theme) => theme.palette.text.primary,
                textTransform: "none",
                fontWeight: 500,
                px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
                py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
                fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
              }}
            >
              Assign to AI
            </Button>
          </Box>
        )
      )}

      {/* Assigned-status pill moved out from under the name to sit beside
          the call icon instead (Rahim, 2026-08-20) — was stacked under the
          name, now lives in the same right-side icon row as CallButton. */}
      {/* ref, not just a wrapper — the Popover anchors to THIS stable node
          (see assignTriggerRef below) instead of whichever inner element
          was clicked, which swaps DOM identity across renderAssignedStatus()'s
          different branches (avatar+name vs plain text) and left the Popover
          anchored to a now-detached node mid-flow — MUI resolves that to
          (0,0), which is exactly the "glitches to the top-left" Rahim saw
          (2026-08-21). */}
      {/* The QR number this chat is on (contact's own / group's own); renders nothing for non-QR chats. */}
      {!isTeamMemberChat && (
        <ChatNumberBadge connectionId={isGroup ? groupInfo?.connectionId : contactInfo?.connectionId || headerCardConnectionId} sx={{ mr: 0.75 }} />
      )}
      {isGroup ? (
        // "Group Members" box -- replaces the assignment status pill entirely for a group
        // thread (user's explicit ask: hide the team-member assign box, show group members
        // instead), same visual/interaction pattern (clickable pill + Popover) as
        // renderAssignedStatus()'s own AssignBox trigger just below.
        <Box sx={{ mr: { xs: 0.5, sm: 0.75, md: 0.75, lg: 0.75, xl: 0.75 }, flexShrink: 0, mt: "-9.2px" }}>
          <Typography
            variant="body2"
            onClick={(e) => setGroupMembersAnchorEl(e.currentTarget)}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              borderRadius: "8px",
              px: 0.5,
              py: 0.625,
              mx: -0.5,
              my: -0.625,
              "&:hover": {
                backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
              },
              fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
              fontWeight: 500,
            }}
          >
            <Box sx={{ width: 19, height: 19, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, bgcolor: theme.palette.action.selected, color: theme.palette.text.secondary }}>
              <Profile2User size="12" variant="Linear" color="currentColor" />
            </Box>
            {(groupInfo?.participantCount ?? groupInfo?.participants?.length ?? 0)} members
            <ArrowDown2 size="14" color="currentColor" />
          </Typography>
        </Box>
      ) : (
        <Box ref={assignTriggerRef} sx={{ mr: { xs: 0.5, sm: 0.75, md: 0.75, lg: 0.75, xl: 0.75 }, flexShrink: 0, mt: "-9.2px" }}>
          {renderAssignedStatus()}
        </Box>
      )}

      {!isTeamMemberChat && !isGroup && (
        // Local wrapper, not a change to CallButton.jsx itself — that component
        // is reused elsewhere (InboxAll, CallsSection, CallsSettings) with its
        // own spacing, so the gap belongs here, not baked into the shared
        // component (Rahim, 2026-08-21: "add space at left and right side").
        <Box sx={{ mx: { xs: 0.5, sm: 0.75, md: 0.75, lg: 0.75, xl: 0.75 }, mt: "-7.7px" }}>
          <CallButton contactInfo={contactInfo} />
        </Box>
      )}

      {/* Render the Close button for Assigned and Unassigned contacts —
          outlined/neutral instead of a filled green pill, matching the
          reference style Rahim gave (2026-08-21: light bordered button,
          tick-in-circle icon, not a loud solid-color CTA). */}
      {(contactInfo?.status === "Assigned" || contactInfo?.status === "Unassigned") && (
        <Box onClick={handleCloseConversation} sx={{ mt: "-7.7px" }}>
          <Button
            variant="outlined"
            size="small"
            disabled={closingConversation}
            startIcon={
              <Box sx={{ width: { xs: 16, sm: 18, md: 18, lg: 18, xl: 18 }, height: { xs: 16, sm: 18, md: 18, lg: 18, xl: 18 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TickCircleIx size="100%" color="currentColor" variant="Linear" />
              </Box>
            }
            sx={{
              mr: { xs: 0.5, sm: 0.75, md: 0.75, lg: 0.75, xl: 0.75 },
              borderRadius: "8px",
              // Matches the navbar's own background (same token used for the
              // navbar's root + ChatInfoRail) instead of .paper, which stood
              // out against it (Rahim, 2026-08-21).
              backgroundColor: (theme) => theme.palette.background.default,
              // Theme's own divider token now, not a custom black/white rgba
              // (Rahim, 2026-08-21: "meet our theme border colour").
              borderColor: (theme) => theme.palette.divider,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.action.hover,
                borderColor: (theme) => theme.palette.divider,
              },
              color: (theme) => theme.palette.text.primary,
              textTransform: "none", // Prevent uppercase transformation
              fontWeight: 500,
              px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
              py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
              fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
            }}
          >
            Close
          </Button>
        </Box>
      )}


      {/* Info icon — still opens the ChatInfo Drawer on XS/SM/MD (untouched,
          only real entry point there). Removed on XL and LG (2026-08-27, LG
          now gets the persistent rail too) instead of just made inert: the
          rail that replaced ChatInfo's inline panel there is persistent, so
          this button has nothing left to do and sitting there unused beside
          Close read as clutter (Rahim, 2026-08-21: "remove the i icon thats
          still there"). */}
      {screenSize !== "xl" && screenSize !== "lg" && (
        <IconButtonWithTooltip
          label="Contact info"
          onClick={() => dispatch(setChatInfo(!showChatInfo))}
          sx={{
            color: (theme) => theme.palette.text.primary,
            padding: { xs: "3px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
            "& svg": {
              fontSize: { xs: "18px", sm: "22px", md: "24px", lg: "26px", xl: "28px" },
            },
          }}
        >
          <InfoIcon />
        </IconButtonWithTooltip>
      )}

      {/* Assign dropdown — anchored to the name/chevron trigger (not a
          centered modal) so it reads as a dropdown, same as every other
          Inbox dropdown. Right-aligned since the trigger sits on the
          right side of the navbar — opening leftward keeps it on-screen
          instead of overflowing past the viewport edge (Rahim, 2026-08-20:
          "align the dropdown properly left or right side"). */}
      <Popover
        open={Boolean(assignAnchorEl)}
        anchorEl={assignAnchorEl}
        onClose={() => {
          setAssignAnchorEl(null);
          dispatch(clearAssignState()); // Clear assign state
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: (theme) => theme.palette.background.default,
              color: (theme) => theme.palette.text.primary,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        <AssignBox
          handleClose={() => {
            setAssignAnchorEl(null);
            dispatch(clearAssignState());
          }}
          onClose={() => {
            setAssignAnchorEl(null);
            dispatch(clearAssignState());
          }}
        />
      </Popover>

      {/* "Assign to AI" agent picker — same anchored-popup formula as the human
          Assign dropdown above. */}
      <Popover
        open={Boolean(aiAssignAnchorEl)}
        anchorEl={aiAssignAnchorEl}
        onClose={() => setAiAssignAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: (theme) => theme.palette.background.default,
              color: (theme) => theme.palette.text.primary,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        <AiAssignBox
          agents={aiAgents}
          loading={aiAgentsLoading}
          assigningId={assigningAiAgentId}
          onSelect={handleAssignToAI}
        />
      </Popover>

      {/* Group Members list — opened from the "N members" pill above (isGroup branch).
          Names + phone numbers only, no click-through to a contact profile: group
          participants are never created as ContactsModel rows (WhatsApp Groups plan
          decision #3), so there is no contact page to link to. */}
      <Popover
        open={Boolean(groupMembersAnchorEl)}
        anchorEl={groupMembersAnchorEl}
        onClose={() => {
          setGroupMembersAnchorEl(null);
          resetAddMemberState();
          setMemberFilterQuery("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // Shifts the whole popover left, off the screen's right edge (bug fix, 2026-09-26:
        // "move members box towards left... add gap from right side" -- with a plain
        // anchorOrigin/transformOrigin "right" pairing, the paper's own right edge sits
        // flush against wherever the trigger button's right edge is, which is itself close
        // to the viewport edge). Popper's own `offset` modifier is the correct, supported way
        // to add real space here -- a margin on the paper wouldn't reliably shift a
        // Popper-positioned element the way it would a normal in-flow box. First value (-20)
        // is the horizontal skid (negative = left, since placement is vertical here); second
        // (8) is the existing vertical gap, now sourced from here instead of the paper's own
        // `mt`, so there's exactly one place controlling this popover's offset, not two.
        slotProps={{
          popper: {
            modifiers: [{ name: "offset", options: { offset: [-20, 8] } }],
          },
          paper: {
            sx: {
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: (theme) => theme.palette.background.default,
              color: (theme) => theme.palette.text.primary,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              width: 360,
              // Scales with the viewport (bug fix, 2026-09-26: a fixed max-height either
              // cramped the list on a tall screen or ran off the bottom on a short one) --
              // caps around two-thirds down the screen rather than reaching the very bottom,
              // same reasoning as a native app's own dropdown sizing. display:flex + the inner
              // list's own flex:1/minHeight:0 (below) is what lets ONLY the member rows scroll
              // while the header/search/add-member controls stay pinned at the top.
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Box sx={{ py: 1, display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}>
              {(groupInfo?.participantCount ?? groupInfo?.participants?.length ?? 0)} members
            </Typography>
            {/* Add/remove requires this workspace's own linked number to be a group admin --
                WhatsApp itself enforces this, not us (see getGroupById.service.js's isSelfAdmin) --
                so the control is simply not offered when it would just fail. */}
            {groupInfo?.isSelfAdmin && (
              <IconButtonWithTooltip
                label="Add member"
                size="small"
                onClick={() => setAddMemberOpen((prev) => !prev)}
                aria-label="Add member"
              >
                <UserAdd size="18" variant="Linear" color="currentColor" />
              </IconButtonWithTooltip>
            )}
          </Box>

          {/* Filters the already-loaded member list -- separate from the add-member search
              above, which searches ClusterDen's own Contacts instead. Always shown (bug fix,
              2026-09-26: previously gated behind >8 members, so a small test group never saw
              it at all -- the add-member contact-picker search is always visible too, so this
              now matches that for consistency). */}
          {groupInfo && (
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter members…"
                value={memberFilterQuery}
                onChange={(e) => setMemberFilterQuery(e.target.value)}
                InputProps={{
                  endAdornment: memberFilterQuery ? (
                    <IconButtonWithTooltip label="Clear filter" size="small" onClick={() => setMemberFilterQuery("")} aria-label="Clear filter">
                      <CloseCircle size="16" variant="Linear" color="currentColor" />
                    </IconButtonWithTooltip>
                  ) : null,
                }}
              />
            </Box>
          )}

          {groupInfo?.isSelfAdmin && addMemberOpen && (
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="Search your contacts…"
                value={addMemberQuery}
                onChange={(e) => setAddMemberQuery(e.target.value)}
                InputProps={{
                  endAdornment: addMemberSearching ? (
                    <CircularProgress size={16} />
                  ) : addMemberQuery ? (
                    <IconButtonWithTooltip label="Clear search" size="small" onClick={() => setAddMemberQuery("")} aria-label="Clear search">
                      <CloseCircle size="16" variant="Linear" color="currentColor" />
                    </IconButtonWithTooltip>
                  ) : null,
                }}
              />
              {addMemberQuery.trim() && !addMemberSearching && (
                <Box sx={{ maxHeight: 180, overflowY: "auto", mt: 0.5 }}>
                  {addMemberResults.length === 0 ? (
                    <Typography variant="caption" sx={{ px: 1, color: (theme) => theme.palette.text.secondary }}>
                      No matching contacts.
                    </Typography>
                  ) : (
                    addMemberResults.map((contact) => (
                      <Box
                        key={contact._id}
                        onClick={() => (addingContactId ? null : handleAddParticipant(contact))}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, borderRadius: "8px",
                          cursor: addingContactId ? "default" : "pointer",
                          opacity: addingContactId && addingContactId !== contact._id ? 0.5 : 1,
                          "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") },
                        }}
                      >
                        <Avatar {...stringAvatar(contact.name || contact.phoneNumberID || "?", AVATAR_SIZES.SMALL)} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {contact.name || "Unknown"}
                          </Typography>
                          {contact.phoneNumberID && (
                            <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>
                              {contact.phoneNumberID}
                            </Typography>
                          )}
                        </Box>
                        {addingContactId === contact._id && <CircularProgress size={16} />}
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* flex:1 + minHeight:0 (bug fix, 2026-09-26: rows were too short/cramped, and the
              list didn't get its own scrollbar so it could grow past the visible screen) --
              this is what makes ONLY this list scroll, independent of the header/search/
              add-member controls above, filling whatever space the popover's own maxHeight
              (70vh, set above) leaves for it -- however many members that turns out to be,
              not a hardcoded pixel guess. */}
          <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {!groupInfo ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <Skeleton variant="rectangular" width="80%" height={20} />
              </Box>
            ) : (groupInfo.participants || []).length === 0 ? (
              <Typography variant="body2" sx={{ px: 2, py: 2, color: (theme) => theme.palette.text.secondary }}>
                No members found.
              </Typography>
            ) : filteredGroupMembers.length === 0 ? (
              <Typography variant="body2" sx={{ px: 2, py: 2, color: (theme) => theme.palette.text.secondary }}>
                No members match "{memberFilterQuery}".
              </Typography>
            ) : (
              filteredGroupMembers.map((p) => (
                <Box
                  key={p.jid || p.phoneNumberID}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                    },
                  }}
                >
                  <Avatar {...stringAvatar(p.name || p.phoneNumberID || "?", AVATAR_SIZES.LARGE)} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* Explicit fontSize, not just a bigger variant (bug fix, 2026-09-26: body1/
                        body2 alone still read as small next to the bigger avatar) -- 1rem/0.9rem
                        is a real, visible step up from MUI's own body1(0.875rem)/body2(0.875rem)
                        via this theme, not just a relative bump. */}
                    <Typography sx={{ fontWeight: 500, fontSize: "1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p.name || p.phoneNumberID || "Unknown"}
                      {(p.isAdmin || p.isSuperAdmin) && (
                        <Typography component="span" sx={{ ml: 0.75, fontSize: "0.85rem", color: (theme) => theme.palette.text.secondary }}>
                          {p.isSuperAdmin ? "(owner)" : "(admin)"}
                        </Typography>
                      )}
                    </Typography>
                    {p.phoneNumberID && (
                      <Typography sx={{ fontSize: "0.9rem", color: (theme) => theme.palette.text.secondary }}>
                        {p.phoneNumberID}
                      </Typography>
                    )}
                  </Box>
                  {groupInfo?.isSelfAdmin && (
                    <IconButtonWithTooltip
                      label="Remove from group"
                      size="small"
                      disabled={removingJid === p.jid}
                      onClick={() => handleRemoveParticipant(p)}
                      aria-label="Remove member"
                    >
                      {removingJid === p.jid ? (
                        <CircularProgress size={16} />
                      ) : (
                        <UserRemove size="18" variant="Linear" color="currentColor" />
                      )}
                    </IconButtonWithTooltip>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default ChatboxNavbar;
